import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { AuthenticatedUser, InvoiceDetail } from '@faktorplus/shared';
import { useInvoice, useRestoreInvoice } from '../../api/invoices';
import { useCurrentUser } from '../../auth/auth-provider';
import { Button } from '../../components/ui/button';
import { ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import { formatDate } from '../../lib/format';
import { AttachmentUpload } from './attachment-upload';
import { CorrectionCard } from './correction-actions';
import { InvoiceSummary } from './invoice-summary';
import { InvoiceView } from './invoice-view';
import { ManagerActions } from './manager-actions';
import { hasOfficeActions, OfficeActions } from './office-actions';
import { StatusHistory } from './status-history';
import styles from './invoice-detail.module.css';
import pageStyles from '../../components/layout/page.module.css';

/**
 * One invoice, seen by whoever opened it.
 *
 * The layout is the same for everyone; what changes is which actions appear,
 * driven by the invoice's state and the viewer's role.
 */
export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: invoice, isLoading, error } = useInvoice(id);

  // Going back through history preserves the filters the register keeps in its
  // query string — a hard link to /faktury threw them away, so reviewing ten
  // filtered invoices meant re-entering the filters ten times.
  // `key === 'default'` means this page was opened directly (an e-mail link),
  // where there is no history entry to return to.
  const cameFromInsideApp = location.key !== 'default';

  const back = cameFromInsideApp ? (
    <button type="button" className={styles.back} onClick={() => void navigate(-1)}>
      ← {backLabel(user)}
    </button>
  ) : (
    <Link to={backTarget(user)} className={styles.back}>
      ← {backLabel(user)}
    </Link>
  );

  if (isLoading) {
    return (
      <>
        {back}
        <LoadingBlock />
      </>
    );
  }

  if (error || !invoice) {
    return (
      <>
        {back}
        <ErrorBox>
          {error instanceof ApiError ? error.message : 'Nie udało się otworzyć faktury.'}
        </ErrorBox>
      </>
    );
  }

  const isOffice = user.role === 'ADMIN' || user.role === 'ACCOUNTANT';
  const isDeleted = invoice.deletedAt !== null;
  // A correction has no workflow: instead of the office cards it gets the one
  // card that matters — settling its delta.
  const isCorrection = invoice.correctsInvoiceId !== null;
  // A deleted invoice is read-only: the API refuses every action on it, so the
  // page offers none — only the banner with its restore button.
  const canManagerDecide = !isDeleted && !isCorrection && canDecideAsManager(invoice, user);
  const showCorrectionCard = isCorrection && !isDeleted && isOffice;
  const hasActions =
    canManagerDecide ||
    showCorrectionCard ||
    (!isDeleted && !isCorrection && hasOfficeActions(invoice, user));

  // After acting, the manager goes back to the queue: the next invoice is the
  // point, not this one's new status.
  const handleDone = () => {
    if (user.role === 'SITE_MANAGER') void navigate('/');
  };

  return (
    <>
      {back}

      {isDeleted && <DeletedBanner invoice={invoice} canRestore={isOffice} />}

      <div className={`${styles.layout} ${hasActions ? '' : styles.layoutSolo}`}>
        {/* Actions come first in the DOM, so that on a phone the approve
            buttons sit above the tall scan instead of a screen below it. */}
        {hasActions && (
          <div className={`${styles.column} ${styles.actionColumn}`}>
            {canManagerDecide && <ManagerActions invoice={invoice} onDone={handleDone} />}
            {showCorrectionCard && <CorrectionCard invoice={invoice} />}
            {!isCorrection && <OfficeActions invoice={invoice} user={user} onDone={handleDone} />}
          </div>
        )}

        <div className={`${styles.column} ${styles.documentColumn}`}>
          <InvoiceSummary invoice={invoice} />
          <InvoiceView
            invoice={invoice}
            upload={isOffice && !isDeleted ? <AttachmentUpload invoice={invoice} /> : undefined}
          />
        </div>

        <div className={styles.historyRow}>
          <StatusHistory entries={invoice.statusHistory} />
        </div>
      </div>
    </>
  );
}

/**
 * Says out loud that this document sits in "Usunięte" — and, for the office,
 * offers the one action a deleted invoice has. The restore can fail with a
 * readable conflict (the same number was keyed in again meanwhile), so the
 * error is shown right here rather than swallowed.
 */
function DeletedBanner({
  invoice,
  canRestore,
}: {
  invoice: InvoiceDetail;
  canRestore: boolean;
}) {
  const restore = useRestoreInvoice();

  return (
    <div className={pageStyles.banner} role="alert">
      <span aria-hidden="true">🗑</span>
      <span>
        <b>Faktura usunięta {formatDate(invoice.deletedAt)}</b>
        {invoice.deletedByName && <> przez {invoice.deletedByName}</>}. Jest widoczna tylko w
        „Usuniętych" i nie liczy się do żadnych zestawień.
        {restore.error && (
          <>
            {' '}
            <b>
              {restore.error instanceof ApiError
                ? restore.error.message
                : 'Nie udało się przywrócić faktury.'}
            </b>
          </>
        )}
      </span>
      {canRestore && (
        <Button isLoading={restore.isPending} onClick={() => restore.mutate(invoice.id)}>
          Przywróć
        </Button>
      )}
    </div>
  );
}

/**
 * Approving requires being the manager the invoice was booked to — not merely
 * having access to the site. Showing the buttons on any visible invoice makes
 * the API answer 403 on a tap, which reads as a broken app rather than as a
 * permission rule.
 *
 * The owner may act in a manager's place; the API allows it and it is how a
 * stuck invoice gets unblocked.
 */
function canDecideAsManager(invoice: InvoiceDetail, user: AuthenticatedUser): boolean {
  if (invoice.status !== 'ASSIGNED') return false;
  if (user.role === 'ADMIN') return true;
  if (user.role !== 'SITE_MANAGER') return false;

  return invoice.allocations.some((allocation) => allocation.managerId === user.id);
}

function backTarget(user: AuthenticatedUser): string {
  return user.role === 'SITE_MANAGER' ? '/' : '/faktury';
}

function backLabel(user: AuthenticatedUser): string {
  return user.role === 'SITE_MANAGER' ? 'Wróć do listy' : 'Wróć do rejestru';
}
